import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { auth } from '../../../../../firebase/config';
import Modal from '../../../../../components/Modal';
import TextField from '../../../../../components/TextField';
import Button from '../../../../../components/Button';
import { FiMapPin, FiPhone, FiHash, FiLock, FiLoader } from 'react-icons/fi';
import Icon from '../../../../../components/Icon';
import SearchableSelect from '../../../../../components/SearchableSelect';
import PakistanPlaceSearch from '../../../../../components/PakistanPlaceSearch';
import BranchMapPickerModal from '../../../../../components/BranchMapPickerModal';
import { fetchNearestPoliceStation } from '../../../../../utils/fetchNearestPoliceStation';
import { fetchNearestAreaLabel, fetchNearbyBankNamesCsv } from '../../../../../utils/fetchNearestLocationAndBanks';
import {
    fetchLocations,
    fetchBankInfo,
    fetchManagers,
    submitBranch,
    BRANCH_TYPE_OPTIONS,
    COVERED_AREA_OPTIONS,
    SRA_LEVEL_OPTIONS,
    defaultExtendedBranchForm,
    type Location,
    type Bank,
    type Manager,
    type BranchFormData,
} from '../service/addBranchService';
import { haversineDistanceKm, formatDistanceKm } from '../../../../../utils/haversineDistanceKm';
import { fetchDrivingDistanceMeters, formatDrivingKmLabel } from '../../../../../utils/fetchDrivingDistanceMeters';

type PSO = {
    id: string;
    name: string;
    psoId?: string;
};

type AddBranchModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    bankId?: string;
};

/** Equal-width segmented Yes/No for form rows */
function YesNoSwitch({
    label,
    value,
    onChange,
    disabled,
}: {
    label: string;
    value: boolean;
    onChange: (v: boolean) => void;
    disabled?: boolean;
}) {
    return (
        <div className="flex min-w-0 w-full flex-col gap-1.5">
            <span className="form-field-label leading-tight">{label}</span>
            <div
                role="group"
                aria-label={label}
                className={`grid h-9 w-full grid-cols-2 overflow-hidden rounded-lg border border-gray-300 bg-gray-100 shadow-sm dark:border-gray-600 dark:bg-gray-900/40 ${
                    disabled ? 'pointer-events-none opacity-50' : ''
                }`}
            >
                <button
                    type="button"
                    onClick={() => onChange(false)}
                    className={`text-xs font-semibold tracking-wide transition ${
                        !value
                            ? 'bg-gray-600 text-white dark:bg-gray-600'
                            : 'bg-transparent text-gray-500 hover:bg-gray-200/80 dark:text-gray-400 dark:hover:bg-gray-800/80'
                    }`}
                >
                    No
                </button>
                <button
                    type="button"
                    onClick={() => onChange(true)}
                    className={`text-xs font-semibold tracking-wide transition ${
                        value
                            ? 'bg-emerald-600 text-white dark:bg-emerald-600'
                            : 'bg-transparent text-gray-500 hover:bg-gray-200/80 dark:text-gray-400 dark:hover:bg-gray-800/80'
                    }`}
                >
                    Yes
                </button>
            </div>
        </div>
    );
}

function FormRow({ children, cols = 4 }: { children: React.ReactNode; cols?: 2 | 3 | 4 | 5 }) {
    const grid =
        cols === 2
            ? 'sm:grid-cols-2'
            : cols === 3
              ? 'sm:grid-cols-2 lg:grid-cols-3'
              : cols === 5
                ? 'sm:grid-cols-2 lg:grid-cols-5'
                : 'sm:grid-cols-2 lg:grid-cols-4';
    return <div className={`grid w-full grid-cols-1 gap-3 ${grid}`}>{children}</div>;
}

function FormCell({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
    return <div className={`min-w-0 ${className}`}>{children}</div>;
}

function openMaps(latStr: string, lonStr: string) {
    const lat = parseFloat(String(latStr).replace(/,/g, '').trim());
    const lng = parseFloat(String(lonStr).replace(/,/g, '').trim());
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank', 'noopener,noreferrer');
}

const baseForm = (): BranchFormData => ({
    branchCode: '',
    address: '',
    mobile: '',
    locationId: '',
    securityCode: '',
    psoId: '',
    branchManagerId: '',
    operationalManagerId: '',
    areaManagerId: '',
    customLocationName: '',
    ...defaultExtendedBranchForm(),
});

export default function AddBranchModal({ isOpen, onClose, onSuccess, bankId }: AddBranchModalProps) {
    const gmapsKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const [mapPickerOpen, setMapPickerOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [locations, setLocations] = useState<Location[]>([]);
    const [banks, setBanks] = useState<Bank[]>([]);
    const [managers, setManagers] = useState<Manager[]>([]);
    const [psos, setPsos] = useState<PSO[]>([]);
    const [selectedBankId, setSelectedBankId] = useState<string>('');
    const [formData, setFormData] = useState<BranchFormData>(baseForm);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [clientCodeReadOnly, setClientCodeReadOnly] = useState(false);
    const [policeLookupLoading, setPoliceLookupLoading] = useState(false);

    const fillNearbyFromCoords = useCallback(
        async (lat: number, lng: number) => {
            const key = gmapsKey?.trim();
            if (!key) return;
            setPoliceLookupLoading(true);
            try {
                const [police, areaLabel, banksCsv] = await Promise.all([
                    fetchNearestPoliceStation(key, lat, lng),
                    fetchNearestAreaLabel(key, lat, lng),
                    fetchNearbyBankNamesCsv(key, lat, lng),
                ]);
                setFormData((prev) => ({
                    ...prev,
                    nearestPoliceStationName: police?.name ?? '',
                    nearestPoliceStationAddress: police?.address ?? '',
                    nearestPoliceStationPhone: police?.phone ?? '',
                    landmark: areaLabel ?? '',
                    nearestOtherBanksBranches: banksCsv ?? '',
                }));
            } finally {
                setPoliceLookupLoading(false);
            }
        },
        [gmapsKey]
    );

    const patchForm = (patch: Partial<BranchFormData>) => {
        setFormData((prev) => ({ ...prev, ...patch }));
        const keys = Object.keys(patch) as (keyof BranchFormData)[];
        if (keys.some((k) => errors[k as string])) {
            setErrors((prev) => {
                const next = { ...prev };
                keys.forEach((k) => {
                    delete next[k as string];
                });
                return next;
            });
        }
    };

    useEffect(() => {
        if (!isOpen) return;

        const loadData = async () => {
            try {
                const locList = await fetchLocations();
                setLocations(locList);

                const uid = auth.currentUser?.uid;
                if (!uid) return;

                const actualBankId = bankId || uid;
                setSelectedBankId(actualBankId);

                const bankInfo = await fetchBankInfo(actualBankId);
                if (bankInfo) {
                    setBanks([bankInfo]);
                    const preset = bankInfo.defaultClientCode?.trim();
                    if (preset) {
                        setFormData((prev) => ({
                            ...prev,
                            securityCode: prev.securityCode.trim() || preset,
                        }));
                        setClientCodeReadOnly(true);
                    } else {
                        setClientCodeReadOnly(false);
                    }
                } else {
                    setClientCodeReadOnly(false);
                }
            } catch (err) {
                console.error('Error loading data:', err);
            }
        };

        loadData();
    }, [isOpen, bankId]);

    useEffect(() => {
        if (!selectedBankId) {
            setManagers([]);
            return;
        }

        const loadManagers = async () => {
            try {
                const managerList = await fetchManagers(selectedBankId);
                setManagers(managerList);

                const { collection, getDocs } = await import('firebase/firestore');
                const { db } = await import('../../../../../firebase/config');
                const psoSnap = await getDocs(collection(db, 'banks', selectedBankId, 'pos'));
                const psoList = psoSnap.docs.map(
                    (d) =>
                        ({
                            id: d.id,
                            ...d.data(),
                        } as PSO)
                );
                setPsos(psoList);
            } catch (err) {
                console.error('Error loading operational data:', err);
                setManagers([]);
                setPsos([]);
            }
        };

        loadManagers();
    }, [selectedBankId]);

    /** Distance from region anchor to branch: driving km (Directions), same as map modal; fallback straight-line. */
    useEffect(() => {
        if (!isOpen) return;
        const id = formData.locationId;
        if (!id || id === 'custom') return;
        const loc = locations.find((l) => l.id === id);
        const refLat = loc?.location?.lat;
        const refLng = loc?.location?.long;
        if (refLat == null || refLng == null) return;
        const rLat = Number(refLat);
        const rLng = Number(refLng);
        if (Number.isNaN(rLat) || Number.isNaN(rLng)) return;

        const bLat = parseFloat(String(formData.latitude).replace(/,/g, '').trim());
        const bLng = parseFloat(String(formData.longitude).replace(/,/g, '').trim());
        if (Number.isNaN(bLat) || Number.isNaN(bLng)) return;

        const fallbackText = formatDistanceKm(haversineDistanceKm(rLat, rLng, bLat, bLng));
        const key = gmapsKey?.trim();

        let cancelled = false;
        const tid = window.setTimeout(() => {
            void (async () => {
                if (!key) {
                    if (cancelled) return;
                    setFormData((prev) => {
                        if (prev.distanceFromRegion === fallbackText) return prev;
                        return { ...prev, distanceFromRegion: fallbackText };
                    });
                    return;
                }
                try {
                    const meters = await fetchDrivingDistanceMeters(key, { lat: rLat, lng: rLng }, { lat: bLat, lng: bLng });
                    if (cancelled) return;
                    const text = meters != null ? formatDrivingKmLabel(meters) : fallbackText;
                    setFormData((prev) => {
                        if (prev.distanceFromRegion === text) return prev;
                        return { ...prev, distanceFromRegion: text };
                    });
                } catch {
                    if (cancelled) return;
                    setFormData((prev) => {
                        if (prev.distanceFromRegion === fallbackText) return prev;
                        return { ...prev, distanceFromRegion: fallbackText };
                    });
                }
            })();
        }, 450);

        return () => {
            cancelled = true;
            window.clearTimeout(tid);
        };
    }, [isOpen, formData.locationId, formData.latitude, formData.longitude, locations, gmapsKey]);

    const mapRouteOrigin = useMemo(() => {
        const id = formData.locationId;
        if (!id || id === 'custom') return null;
        const loc = locations.find((l) => l.id === id);
        const la = loc?.location?.lat;
        const lo = loc?.location?.long;
        if (la == null || lo == null) return null;
        const nla = Number(la);
        const nlo = Number(lo);
        if (Number.isNaN(nla) || Number.isNaN(nlo)) return null;
        return { lat: nla, lng: nlo };
    }, [formData.locationId, locations]);

    const handleInputChange = (field: string, value: string) => {
        patchForm({ [field]: value } as Partial<BranchFormData>);
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.branchCode.trim()) {
            newErrors.branchCode = 'Branch code is required';
        }
        if (!formData.address.trim()) {
            newErrors.address = 'Branch name is required';
        }
        if (!formData.mobile.trim()) {
            newErrors.mobile = 'Mobile is required';
        }
        if (!formData.locationId) {
            newErrors.locationId = 'Region is required';
        }
        if (!formData.securityCode.trim()) {
            newErrors.securityCode = 'Client code is required';
        }
        if (formData.branchType === 'Other' && !formData.branchTypeOther.trim()) {
            newErrors.branchTypeOther = 'Specify branch type';
        }
        if (
            (formData.sraLevel === 'orange' || formData.sraLevel === 'red') &&
            !formData.sraReason.trim()
        ) {
            newErrors.sraReason = 'Reason required for Orange / Red';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        const uid = auth.currentUser?.uid;
        if (!uid || !selectedBankId) return;

        setLoading(true);
        try {
            await submitBranch(selectedBankId, formData);
            const preset = banks[0]?.defaultClientCode?.trim();
            setFormData({ ...baseForm(), ...(preset ? { securityCode: preset } : {}) });
            setErrors({});
            onSuccess();
        } catch (err: any) {
            console.error('Error adding branch:', err);
            setErrors({ submit: err.message || 'Failed to add branch. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setMapPickerOpen(false);
        setFormData(baseForm());
        setErrors({});
        setClientCodeReadOnly(false);
        onClose();
    };

    const needSraReason = formData.sraLevel === 'orange' || formData.sraLevel === 'red';

    return (
        <>
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Add New Branch"
            size="wide"
            closeOnBackdropClick={false}
        >
            <form onSubmit={handleSubmit} className="flex flex-col p-4 sm:p-6">
                <div className="space-y-4">
                    {/* 1 — Bank, Branch code, Client code (read-only), Mobile */}
                    <FormRow cols={4}>
                        <FormCell>
                            <label className="form-field-label">Bank</label>
                            <input
                                type="text"
                                value={banks[0]?.name || ''}
                                readOnly
                                className="form-control form-control-readonly"
                                placeholder="—"
                            />
                        </FormCell>
                        <FormCell>
                            <TextField
                                label="Branch code"
                                value={formData.branchCode}
                                onChange={(e) => handleInputChange('branchCode', e.target.value)}
                                placeholder="Code"
                                required
                                prefix={<Icon icon={FiHash} className="text-gray-400" />}
                                className={errors.branchCode ? 'border-red-500' : ''}
                            />
                            {errors.branchCode && (
                                <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">{errors.branchCode}</p>
                            )}
                        </FormCell>
                        <FormCell>
                            <TextField
                                label="Client code"
                                value={formData.securityCode}
                                readOnly={clientCodeReadOnly}
                                onChange={(e) => handleInputChange('securityCode', e.target.value)}
                                placeholder="Client code"
                                required={!clientCodeReadOnly}
                                prefix={<Icon icon={FiLock} className="text-gray-400" />}
                                className={errors.securityCode ? 'border-red-500' : ''}
                            />
                            {errors.securityCode && (
                                <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">{errors.securityCode}</p>
                            )}
                        </FormCell>
                        <FormCell>
                            <TextField
                                label="Mobile"
                                type="tel"
                                value={formData.mobile}
                                onChange={(e) => handleInputChange('mobile', e.target.value)}
                                placeholder="Mobile"
                                required
                                prefix={<Icon icon={FiPhone} className="text-gray-400" />}
                                className={errors.mobile ? 'border-red-500' : ''}
                            />
                            {errors.mobile && (
                                <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">{errors.mobile}</p>
                            )}
                        </FormCell>
                    </FormRow>

                    {/* 2 — Branch type, Cash limit, Region, Bank name (branch name → address) */}
                    <FormRow cols={4}>
                        <FormCell>
                            <label className="form-field-label">Branch type</label>
                            <select
                                value={formData.branchType}
                                onChange={(e) => patchForm({ branchType: e.target.value })}
                                className="form-control"
                            >
                                {BRANCH_TYPE_OPTIONS.map((t) => (
                                    <option key={t} value={t}>
                                        {t}
                                    </option>
                                ))}
                            </select>
                        </FormCell>
                        <FormCell>
                            <TextField
                                label="Cash limit (M PKR)"
                                value={formData.cashLimitMillion}
                                onChange={(e) => handleInputChange('cashLimitMillion', e.target.value)}
                                placeholder="e.g. 2.5"
                                type="text"
                            />
                        </FormCell>
                        <FormCell>
                            <label className="form-field-label">Region</label>
                            <div className="relative">
                                <Icon
                                    icon={FiMapPin}
                                    className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                                />
                                <select
                                    value={formData.locationId}
                                    onChange={(e) => handleInputChange('locationId', e.target.value)}
                                    className="form-control pl-8"
                                >
                                    <option value="">Select region</option>
                                    {locations.map((loc) => (
                                        <option key={loc.id} value={loc.id}>
                                            {loc.name}
                                        </option>
                                    ))}
                                    <option value="custom">+ Custom region</option>
                                </select>
                            </div>
                            {errors.locationId && (
                                <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">{errors.locationId}</p>
                            )}
                        </FormCell>
                        <FormCell>
                            <label className="form-field-label">Branch name</label>
                            <input
                                type="text"
                                value={formData.address}
                                onChange={(e) => handleInputChange('address', e.target.value)}
                                placeholder="Branch name"
                                required
                                className={`form-control ${errors.address ? '!border-red-400' : ''}`}
                            />
                            {errors.address && (
                                <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">{errors.address}</p>
                            )}
                        </FormCell>
                    </FormRow>

                    {formData.branchType === 'Other' && (
                        <FormRow cols={4}>
                            <FormCell className="lg:col-span-2">
                                <TextField
                                    label="Other branch type"
                                    value={formData.branchTypeOther}
                                    onChange={(e) => handleInputChange('branchTypeOther', e.target.value)}
                                    placeholder="Specify"
                                    className={errors.branchTypeOther ? 'border-red-500' : ''}
                                />
                                {errors.branchTypeOther && (
                                    <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">
                                        {errors.branchTypeOther}
                                    </p>
                                )}
                            </FormCell>
                        </FormRow>
                    )}

                    {formData.locationId === 'custom' && (
                        <FormRow cols={4}>
                            <FormCell className="lg:col-span-2">
                                <TextField
                                    label="Custom region"
                                    value={formData.customLocationName || ''}
                                    onChange={(e) => handleInputChange('customLocationName', e.target.value)}
                                    placeholder="Region name"
                                    required
                                />
                            </FormCell>
                        </FormRow>
                    )}

                    {/* 3 — Vantage, Emergency exit, Saturday open, Locker */}
                    <FormRow cols={4}>
                        <FormCell>
                            <YesNoSwitch
                                label="Vantage point"
                                value={formData.vantagePointAvailable}
                                onChange={(v) => patchForm({ vantagePointAvailable: v })}
                            />
                        </FormCell>
                        <FormCell>
                            <YesNoSwitch
                                label="Emergency exit"
                                value={formData.emergencyExitAvailable}
                                onChange={(v) => patchForm({ emergencyExitAvailable: v })}
                            />
                        </FormCell>
                        <FormCell>
                            <YesNoSwitch
                                label="Saturday open"
                                value={formData.saturdayOpen}
                                onChange={(v) => patchForm({ saturdayOpen: v })}
                            />
                        </FormCell>
                        <FormCell>
                            <YesNoSwitch
                                label="Locker"
                                value={formData.lockerAvailable}
                                onChange={(v) => patchForm({ lockerAvailable: v })}
                            />
                        </FormCell>
                    </FormRow>

                    {/* ATM row — switch + type + separate entrance one line; invisible placeholders keep height when ATM off */}
                    <FormRow cols={3}>
                        <FormCell className="flex flex-col justify-end">
                            <YesNoSwitch
                                label="ATM"
                                value={formData.atmAvailable}
                                onChange={(v) =>
                                    patchForm({
                                        atmAvailable: v,
                                        ...(v ? {} : { atmMountType: '', atmSeparateFromMainEntrance: true }),
                                    })
                                }
                            />
                        </FormCell>
                        <FormCell className="flex flex-col justify-end">
                            <div
                                className={
                                    formData.atmAvailable
                                        ? ''
                                        : 'invisible pointer-events-none select-none'
                                }
                                aria-hidden={!formData.atmAvailable}
                            >
                                <label className="form-field-label">ATM type</label>
                                <select
                                    value={formData.atmMountType}
                                    onChange={(e) =>
                                        patchForm({
                                            atmMountType: e.target.value as BranchFormData['atmMountType'],
                                        })
                                    }
                                    className="form-control"
                                    tabIndex={formData.atmAvailable ? 0 : -1}
                                >
                                    <option value="">—</option>
                                    <option value="wall_mounted">Wall mounted</option>
                                    <option value="standalone">Standalone</option>
                                </select>
                            </div>
                        </FormCell>
                        <FormCell className="flex flex-col justify-end">
                            <div
                                className={
                                    formData.atmAvailable
                                        ? ''
                                        : 'invisible pointer-events-none select-none'
                                }
                                aria-hidden={!formData.atmAvailable}
                            >
                                <YesNoSwitch
                                    label="Separate entrance"
                                    value={formData.atmSeparateFromMainEntrance}
                                    onChange={(v) => patchForm({ atmSeparateFromMainEntrance: v })}
                                />
                            </div>
                        </FormCell>
                    </FormRow>

                    {/* 4 — Find location (left), Long, Lat, Distance, map */}
                    <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6 lg:items-end">
                        <FormCell className="min-w-0 sm:col-span-2 lg:col-span-2">
                            <PakistanPlaceSearch
                                apiKey={gmapsKey}
                                onResolved={(p) => {
                                    patchForm({
                                        latitude: String(p.lat),
                                        longitude: String(p.lng),
                                    });
                                    void fillNearbyFromCoords(p.lat, p.lng);
                                }}
                            />
                        </FormCell>
                        <FormCell>
                            <label className="form-field-label">Long</label>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={formData.longitude}
                                onChange={(e) => handleInputChange('longitude', e.target.value)}
                                placeholder="69.07"
                                className="form-control text-sm"
                            />
                        </FormCell>
                        <FormCell>
                            <label className="form-field-label">Lat</label>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={formData.latitude}
                                onChange={(e) => handleInputChange('latitude', e.target.value)}
                                placeholder="24.01"
                                className="form-control text-sm"
                            />
                        </FormCell>
                        <FormCell>
                            <label className="form-field-label">Distance from region</label>
                            <input
                                type="text"
                                value={formData.distanceFromRegion}
                                onChange={(e) => handleInputChange('distanceFromRegion', e.target.value)}
                                placeholder="Auto (km) when region has coordinates"
                                title="Filled from region map pin to branch coordinates; editable"
                                className="form-control text-sm"
                            />
                        </FormCell>
                        <FormCell className="flex flex-col justify-end lg:w-auto">
                            <span className="form-field-label opacity-0 select-none">Map</span>
                            <button
                                type="button"
                                title={gmapsKey?.trim() ? 'Pick on map (Karachi default)' : 'Open in Google Maps'}
                                onClick={() => {
                                    if (gmapsKey?.trim()) setMapPickerOpen(true);
                                    else openMaps(formData.latitude, formData.longitude);
                                }}
                                className="inline-flex h-9 w-full max-w-[2.25rem] items-center justify-center rounded-lg border border-gray-300 bg-gray-100 text-gray-700 shadow-sm transition hover:bg-gray-50 dark:border-gray-600 dark:bg-black/30 dark:text-gray-200 dark:hover:bg-black/50"
                            >
                                <Icon icon={FiMapPin} className="h-4 w-4" />
                            </button>
                        </FormCell>
                    </div>

                    {/* 5 — Covered area, Landmark, Nearest banks */}
                    <FormRow cols={3}>
                        <FormCell>
                            <label className="form-field-label">Covered area</label>
                            <select
                                value={formData.coveredArea}
                                onChange={(e) => patchForm({ coveredArea: e.target.value })}
                                className="form-control"
                            >
                                <option value="">—</option>
                                {COVERED_AREA_OPTIONS.filter(Boolean).map((o) => (
                                    <option key={o} value={o}>
                                        {o}
                                    </option>
                                ))}
                            </select>
                        </FormCell>
                        <FormCell>
                            <TextField
                                label="Landmark"
                                value={formData.landmark}
                                onChange={(e) => handleInputChange('landmark', e.target.value)}
                                placeholder="Area / landmark (auto from map or search)"
                            />
                        </FormCell>
                        <FormCell>
                            <TextField
                                label="Nearest banks"
                                value={formData.nearestOtherBanksBranches}
                                onChange={(e) => handleInputChange('nearestOtherBanksBranches', e.target.value)}
                                placeholder="Meezan, UBL… (auto from map or search)"
                            />
                        </FormCell>
                    </FormRow>

                    {policeLookupLoading && (
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                <Icon icon={FiLoader} className="h-3.5 w-3.5 animate-spin" />
                                Looking up…
                            </span>
                        </div>
                    )}
                    <FormRow cols={3}>
                        <FormCell>
                            <label className="form-field-label">Police Station name</label>
                            <input
                                type="text"
                                value={formData.nearestPoliceStationName}
                                onChange={(e) => handleInputChange('nearestPoliceStationName', e.target.value)}
                                placeholder="Auto or type"
                                className="form-control text-sm"
                            />
                        </FormCell>
                        <FormCell>
                            <label className="form-field-label">Police Station address</label>
                            <input
                                type="text"
                                value={formData.nearestPoliceStationAddress}
                                onChange={(e) => handleInputChange('nearestPoliceStationAddress', e.target.value)}
                                placeholder="Auto or type"
                                className="form-control text-sm"
                            />
                        </FormCell>
                        <FormCell>
                            <label className="form-field-label">Police Station phone</label>
                            <input
                                type="text"
                                value={formData.nearestPoliceStationPhone}
                                onChange={(e) => handleInputChange('nearestPoliceStationPhone', e.target.value)}
                                placeholder="Auto or type"
                                className="form-control text-sm"
                            />
                        </FormCell>
                    </FormRow>

                    {/* 6 — SRA */}
                    <div className="w-full">
                        <span className="form-field-label">SRA level</span>
                        <div className="mt-1 flex flex-wrap gap-2">
                            {SRA_LEVEL_OPTIONS.map((opt) => (
                                <label
                                    key={opt.value}
                                    className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs transition dark:border-gray-600 ${
                                        formData.sraLevel === opt.value
                                            ? 'border-gray-500 bg-white ring-1 ring-gray-400 dark:bg-gray-800'
                                            : 'border-gray-200 bg-gray-50 dark:bg-gray-900/40'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="sra-add"
                                        checked={formData.sraLevel === opt.value}
                                        onChange={() =>
                                            patchForm({
                                                sraLevel: opt.value,
                                                sraReason:
                                                    opt.value === 'orange' || opt.value === 'red'
                                                        ? formData.sraReason
                                                        : '',
                                            })
                                        }
                                        className="sr-only"
                                    />
                                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${opt.dotClass}`} />
                                    <span className="font-medium text-gray-800 dark:text-gray-200">{opt.label}</span>
                                    <span className="text-gray-500 dark:text-gray-400">({opt.hint})</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {needSraReason && (
                        <div className="w-full">
                            <label className="form-field-label">SRA reason</label>
                            <textarea
                                value={formData.sraReason}
                                onChange={(e) => handleInputChange('sraReason', e.target.value)}
                                placeholder="Why Orange / Red"
                                rows={2}
                                className={`form-control resize-y text-sm ${errors.sraReason ? '!border-red-400' : ''}`}
                            />
                            {errors.sraReason && (
                                <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">{errors.sraReason}</p>
                            )}
                        </div>
                    )}

                    {/* 7 — Event affected + reason on one line */}
                    <div className="flex w-full flex-wrap items-end gap-3">
                        <div className="w-full min-w-[8rem] max-w-[11rem] shrink-0">
                            <YesNoSwitch
                                label="Event affected"
                                value={formData.eventAffectedEnabled}
                                onChange={(v) =>
                                    patchForm({
                                        eventAffectedEnabled: v,
                                        ...(!v ? { eventAffectedAreasNote: '' } : {}),
                                    })
                                }
                            />
                        </div>
                        {formData.eventAffectedEnabled && (
                            <FormCell className="min-w-[12rem] flex-1">
                                <label className="form-field-label">Reason</label>
                                <input
                                    type="text"
                                    value={formData.eventAffectedAreasNote}
                                    onChange={(e) => handleInputChange('eventAffectedAreasNote', e.target.value)}
                                    placeholder="e.g. jaloos, route changes…"
                                    className="form-control"
                                />
                            </FormCell>
                        )}
                    </div>

                    {/* 8 — PSO assign, managers */}
                    <FormRow cols={4}>
                        <FormCell>
                            <label className="form-field-label">PSO assign</label>
                            <select
                                value={formData.psoId || ''}
                                onChange={(e) => handleInputChange('psoId', e.target.value)}
                                className="form-control"
                            >
                                <option value="">Select</option>
                                {psos.map((pso) => (
                                    <option key={pso.id} value={(pso as any).psoId || pso.id}>
                                        {pso.name}
                                    </option>
                                ))}
                            </select>
                        </FormCell>
                        {selectedBankId && managers.length > 0 ? (
                            <>
                                <FormCell>
                                    <SearchableSelect
                                        className="w-full min-w-0"
                                        label="Branch manager"
                                        options={managers.map((mgr) => ({
                                            id: mgr.id,
                                            label: mgr.name,
                                            value: mgr.id,
                                            details: { email: mgr.email, mobile: mgr.mobile },
                                        }))}
                                        value={formData.branchManagerId || ''}
                                        onChange={(value) => handleInputChange('branchManagerId', value)}
                                        placeholder="Optional"
                                    />
                                </FormCell>
                                <FormCell>
                                    <SearchableSelect
                                        className="w-full min-w-0"
                                        label="Operations manager"
                                        options={managers.map((mgr) => ({
                                            id: mgr.id,
                                            label: mgr.name,
                                            value: mgr.id,
                                            details: { email: mgr.email, mobile: mgr.mobile },
                                        }))}
                                        value={formData.operationalManagerId || ''}
                                        onChange={(value) => handleInputChange('operationalManagerId', value)}
                                        placeholder="Optional"
                                    />
                                </FormCell>
                                <FormCell>
                                    <SearchableSelect
                                        className="w-full min-w-0"
                                        label="Area manager"
                                        options={managers.map((mgr) => ({
                                            id: mgr.id,
                                            label: mgr.name,
                                            value: mgr.id,
                                            details: { email: mgr.email, mobile: mgr.mobile },
                                        }))}
                                        value={formData.areaManagerId || ''}
                                        onChange={(value) => handleInputChange('areaManagerId', value)}
                                        placeholder="Optional"
                                    />
                                </FormCell>
                            </>
                        ) : (
                            <>
                                <FormCell />
                                <FormCell />
                                <FormCell />
                            </>
                        )}
                    </FormRow>

                    {errors.submit && (
                        <div className="w-full rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
                            {errors.submit}
                        </div>
                    )}

                    <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                        <Button type="button" variant="secondary" onClick={handleClose} disabled={loading} className="!py-2">
                            Cancel
                        </Button>
                        <Button type="submit" disabled={loading} className="!py-2">
                            {loading ? 'Adding…' : 'Add Branch'}
                        </Button>
                    </div>
                </div>
            </form>
        </Modal>
        <BranchMapPickerModal
            isOpen={mapPickerOpen}
            onClose={() => setMapPickerOpen(false)}
            apiKey={gmapsKey}
            routeOrigin={mapRouteOrigin}
            latitude={formData.latitude}
            longitude={formData.longitude}
            onSubmit={(lat, lng) => {
                patchForm({ latitude: String(lat), longitude: String(lng) });
                void fillNearbyFromCoords(lat, lng);
            }}
        />
        </>
    );
}
